import { PopoverPosition, Spinner, SpinnerSize } from '@blueprintjs/core';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import { TEST_IDS, getModelItemTestId } from 'src/constants/testIds';
import { databaseSelector } from 'src/selectors/database';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import { ModelSelectItem, modelItemsSelector } from 'src/selectors/modelItems';
import { namedGraphsByModelSelector } from 'src/selectors/namedGraphsByModel';
import { publishableDefaultModelNamedGraph } from 'src/selectors/publishableDefaultModelNamedGraph';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import {
  DEFAULT_MODEL,
  SPECIAL_NAMED_GRAPH_IRIS,
} from 'src/templates/queries/iris';
import { colors } from 'src/templates/styles/colors';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type ModelSelectorProps = {
  databaseId: string;
  disabled?: boolean;
  popoverPosition?: PopoverPosition;
  selectedModel: ModelSelectItem | null;
  setSelectedModel: (item: ModelSelectItem | null) => void;
};

/**
 * (single-item) selector for models in a database
 *
 * This is used for exporting/publishing only (for now), so we will filter out the
 * "default" model and filter out models using any "special named graphs".
 */
export const ModelSelector: React.VFC<ModelSelectorProps> = ({
  databaseId,
  disabled,
  popoverPosition,
  selectedModel,
  setSelectedModel,
}) => {
  const namedGraphsByModel = useReduxSelector(
    namedGraphsByModelSelector(databaseId)
  );
  const defaultNamedGraph = useReduxSelector(
    publishableDefaultModelNamedGraph(databaseId)
  );
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const isDatabaseRefreshed = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const database = useReduxSelector(databaseSelector(databaseId));
  const isConnected = useReduxSelector(isConnectedSelector);
  const allModelItems = useReduxSelector(modelItemsSelector(databaseId));

  const modelItems = allModelItems.filter(
    (item) =>
      // Default model (if allowed) is prepended separately
      item.value !== DEFAULT_MODEL &&
      namedGraphsByModel[item.value]?.length >= 1 &&
      // don't allow models with special named graphs
      namedGraphsByModel[item.value].every(
        (iri) => !SPECIAL_NAMED_GRAPH_IRIS.includes(iri)
      )
  );

  if (defaultNamedGraph !== null) {
    modelItems.unshift({
      text: DEFAULT_MODEL,
      value: DEFAULT_MODEL,
      namedGraph: defaultNamedGraph.iri,
    });
  }

  const isSelectorDisabled =
    !isConnected ||
    isRefreshingDatabases ||
    (databaseId && !isDatabaseRefreshed) ||
    disabled;

  const handleSelectModel = (
    item: SelectItem | null,
    evt?: React.MouseEvent
  ) => {
    stopPropagation(evt);
    if (item) {
      setSelectedModel(item);
    } else {
      setSelectedModel(null);
    }
  };

  return (
    <ItemSelector.Single
      buttonIcon={icons.model}
      buttonIconColor={selectedModel ? colors.darkIcon : undefined}
      buttonRightIcon={
        isRefreshingDatabases || (database && !isDatabaseRefreshed) ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : undefined
      }
      buttonText={
        selectedModel?.text || copy.components.cards.model.placeholder
      }
      disabled={isSelectorDisabled}
      filterInputProps={{
        placeholder: copy.components.defaults.filter,
      }}
      items={modelItems}
      itemTestId={getModelItemTestId}
      onButtonClick={stopPropagation}
      onClear={stopPropagation}
      onSelect={handleSelectModel}
      popoverProps={{
        backdropProps: { onClick: stopPropagation },
        position: popoverPosition,
      }}
      selected={selectedModel}
      testId={TEST_IDS.model.selector}
    />
  );
};
